// The HQ Connector, as the Source screen needs it: which servers it is for,
// which download suits this computer, and the download itself.
import type { DataMapperConnectorDownload, DataMapperConnectorPlatform } from "../dataMapper.types";
import { bearerHeaders, saveBlobInSession } from "shared/sessionDownload";

/**
 * The server names that mean "the SQL Server on my own computer", with or
 * without a named instance (localhost\SQLEXPRESS). Kept in step with the API's
 * ON_MY_MACHINE_HOSTS (data-plane/sql-host-policy.ts): HQ sends these to the
 * person's own connector.
 */
const ON_MY_MACHINE = new Set(["localhost", "127.0.0.1", "::1", "host.docker.internal"]);

export function isOnMyMachineServer(server: string): boolean {
  const host = server.split("\\")[0].trim().toLowerCase().replace(/^\[(.*)\]$/, "$1").replace(/\.$/, "");
  return ON_MY_MACHINE.has(host);
}

/**
 * The download that suits the browser's computer, when it can tell: Windows,
 * or a Mac (Apple silicon is assumed: browsers do not say, and every Mac sold
 * since 2023 is one; the Intel build is offered beside it).
 */
export function suggestedPlatform(
  nav: { userAgent?: string; platform?: string } | undefined = typeof navigator === "undefined" ? undefined : navigator
): DataMapperConnectorPlatform | null {
  const ua = `${nav?.platform ?? ""} ${nav?.userAgent ?? ""}`.toLowerCase();
  if (ua.includes("win")) return "windows-x64";
  if (ua.includes("mac")) return "macos-arm64";
  return null;
}

/** The downloads, the one that suits this computer first. */
export function orderDownloads(
  downloads: DataMapperConnectorDownload[],
  suggested: DataMapperConnectorPlatform | null
): DataMapperConnectorDownload[] {
  return [...downloads].sort((a, b) => Number(b.platform === suggested) - Number(a.platform === suggested));
}

/** What the connector's platform is called on screen. */
export function platformName(platform: string | undefined): string {
  if (platform === "win32") return "Windows";
  if (platform === "darwin") return "Mac";
  if (platform === "linux") return "Linux";
  return platform ?? "an unknown system";
}

/**
 * Download one connector zip with the person's own session (the route is for
 * signed-in operators; a plain link would carry no token), then save it —
 * unless `ended()` says that session has since signed out: a raw fetch the
 * session guard cannot see, so it is asked after every await. Resolves true
 * once the zip was handed to the browser, false when the sign-out stopped it.
 */
export async function downloadConnectorZip(
  apiUrl: string,
  token: string | null,
  download: DataMapperConnectorDownload,
  ended: () => boolean
): Promise<boolean> {
  const response = await fetch(`${apiUrl}/data-mapper-connector/download/${encodeURIComponent(download.platform)}`, {
    headers: bearerHeaders(token),
  });
  if (ended()) return false;
  if (!response.ok) {
    let message = `The download failed (${response.status}).`;
    try {
      const body = (await response.json()) as { message?: unknown };
      if (typeof body?.message === "string" && body.message) message = body.message;
    } catch {
      // Not JSON: the status line says it.
    }
    throw new Error(message);
  }
  // shortcut: the zip (about 40 MB) is held in the page's memory before it is saved, as the Bulk
  // Export download does; stream it to disk (File System Access API) if it ever grows much.
  const blob = await response.blob();
  return saveBlobInSession(ended, blob, download.fileName);
}
